import { useEffect, useRef, useCallback } from "react";

/**
 * Custom hook for managing accessibility features in notification components
 * Handles screen reader announcements, focus management, and keyboard navigation
 */
const useAccessibility = ({
  announceChanges = true,
  manageFocus = true,
  enableKeyboardNavigation = true,
}) => {
  const announcementRef = useRef(null);
  const focusableElementsRef = useRef([]);
  const currentFocusIndexRef = useRef(-1);

  // Create live region for announcements if it doesn't exist
  useEffect(() => {
    if (!announceChanges) return;

    let liveRegion = document.getElementById("notification-announcements");

    if (!liveRegion) {
      liveRegion = document.createElement("div");
      liveRegion.id = "notification-announcements";
      liveRegion.setAttribute("aria-live", "polite");
      liveRegion.setAttribute("aria-atomic", "true");
      liveRegion.className = "sr-only";
      liveRegion.style.cssText = `
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      `;
      document.body.appendChild(liveRegion);
    }

    announcementRef.current = liveRegion;

    return () => {
      // Clean up on unmount
      if (liveRegion && liveRegion.parentNode) {
        liveRegion.parentNode.removeChild(liveRegion);
      }
    };
  }, [announceChanges]);

  // Announce message to screen readers
  const announce = useCallback(
    (message, priority = "polite") => {
      if (!announceChanges || !announcementRef.current) return;

      const liveRegion = announcementRef.current;

      // Update aria-live attribute based on priority
      liveRegion.setAttribute("aria-live", priority);

      // Clear and set new message
      liveRegion.textContent = "";
      setTimeout(() => {
        liveRegion.textContent = message;
      }, 100);
    },
    [announceChanges]
  );

  // Update focusable elements list
  const updateFocusableElements = useCallback(
    (container) => {
      if (!manageFocus || !container) return;

      const focusableSelectors = [
        "button:not([disabled])",
        "input:not([disabled])",
        "select:not([disabled])",
        "textarea:not([disabled])",
        "a[href]",
        '[tabindex]:not([tabindex="-1"])',
        '[role="button"]:not([disabled])',
        '[role="menuitem"]:not([disabled])',
      ].join(", ");

      const elements = Array.from(
        container.querySelectorAll(focusableSelectors)
      );
      focusableElementsRef.current = elements;

      return elements;
    },
    [manageFocus]
  );

  // Focus management
  const focusElement = useCallback(
    (element) => {
      if (!manageFocus || !element) return;

      element.focus();

      // Scroll element into view if needed
      element.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    },
    [manageFocus]
  );

  // Focus first element
  const focusFirst = useCallback(
    (container) => {
      const elements = updateFocusableElements(container);
      if (elements.length > 0) {
        currentFocusIndexRef.current = 0;
        focusElement(elements[0]);
      }
    },
    [updateFocusableElements, focusElement]
  );

  // Focus last element
  const focusLast = useCallback(
    (container) => {
      const elements = updateFocusableElements(container);
      if (elements.length > 0) {
        currentFocusIndexRef.current = elements.length - 1;
        focusElement(elements[elements.length - 1]);
      }
    },
    [updateFocusableElements, focusElement]
  );

  // Focus next element
  const focusNext = useCallback(() => {
    const elements = focusableElementsRef.current;
    if (elements.length === 0) return;

    currentFocusIndexRef.current =
      (currentFocusIndexRef.current + 1) % elements.length;
    focusElement(elements[currentFocusIndexRef.current]);
  }, [focusElement]);

  // Focus previous element
  const focusPrevious = useCallback(() => {
    const elements = focusableElementsRef.current;
    if (elements.length === 0) return;

    currentFocusIndexRef.current =
      currentFocusIndexRef.current <= 0
        ? elements.length - 1
        : currentFocusIndexRef.current - 1;
    focusElement(elements[currentFocusIndexRef.current]);
  }, [focusElement]);

  // Keyboard navigation handler
  const handleKeyboardNavigation = useCallback(
    (event, container) => {
      if (!enableKeyboardNavigation) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          focusNext();
          break;
        case "ArrowUp":
          event.preventDefault();
          focusPrevious();
          break;
        case "Home":
          event.preventDefault();
          focusFirst(container);
          break;
        case "End":
          event.preventDefault();
          focusLast(container);
          break;
        case "Escape":
          // Let parent components handle escape
          break;
        default:
          break;
      }
    },
    [enableKeyboardNavigation, focusNext, focusPrevious, focusFirst, focusLast]
  );

  // Generate accessible props for components
  const getAccessibleProps = useCallback((type, options = {}) => {
    const baseProps = {};

    switch (type) {
      case "notification-bell":
        return {
          ...baseProps,
          "aria-label": options.unreadCount
            ? `Notifications, ${options.unreadCount} unread`
            : "Notifications",
          "aria-expanded": options.isOpen || false,
          "aria-haspopup": "menu",
          role: "button",
        };

      case "notification-dropdown":
        return {
          ...baseProps,
          role: "menu",
          "aria-label": "Notifications menu",
          "aria-orientation": "vertical",
        };

      case "notification-item":
        return {
          ...baseProps,
          role: "menuitem",
          tabIndex: 0,
          "aria-label": options.message
            ? `${options.message}, ${options.read ? "read" : "unread"}`
            : "Notification",
          "aria-describedby": options.timestampId,
        };

      case "notification-list":
        return {
          ...baseProps,
          role: "list",
          "aria-label": options.title || "Notifications",
          "aria-live": "polite",
          "aria-busy": options.loading || false,
        };

      case "bulk-actions":
        return {
          ...baseProps,
          role: "toolbar",
          "aria-label": "Notification actions",
        };

      case "filter-controls":
        return {
          ...baseProps,
          role: "group",
          "aria-label": "Filter notifications",
        };

      default:
        return baseProps;
    }
  }, []);

  // Announce notification changes
  const announceNotificationChange = useCallback(
    (type, details = {}) => {
      if (!announceChanges) return;

      let message = "";

      switch (type) {
        case "marked-read":
          message =
            details.count > 1
              ? `${details.count} notifications marked as read`
              : "Notification marked as read";
          break;
        case "deleted":
          message =
            details.count > 1
              ? `${details.count} notifications deleted`
              : "Notification deleted";
          break;
        case "new-notification":
          message = `New notification: ${details.message}`;
          break;
        case "unread-count-changed":
          message =
            details.count > 0
              ? `${details.count} unread notifications`
              : "No unread notifications";
          break;
        case "filter-applied":
          message = `Filter applied: ${details.filter}`;
          break;
        case "loading":
          message = "Loading notifications";
          break;
        case "error":
          message = `Error: ${details.message}`;
          break;
        default:
          message = details.message || "";
      }

      if (message) {
        announce(message, details.priority || "polite");
      }
    },
    [announce, announceChanges]
  );

  // High contrast mode detection
  const isHighContrast = useCallback(() => {
    if (typeof window === "undefined") return false;

    return (
      window.matchMedia("(prefers-contrast: high)").matches ||
      window.matchMedia("(-ms-high-contrast: active)").matches
    );
  }, []);

  // Reduced motion detection
  const prefersReducedMotion = useCallback(() => {
    if (typeof window === "undefined") return false;

    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  return {
    // Announcement functions
    announce,
    announceNotificationChange,

    // Focus management
    focusFirst,
    focusLast,
    focusNext,
    focusPrevious,
    focusElement,
    updateFocusableElements,

    // Keyboard navigation
    handleKeyboardNavigation,

    // Accessible props generator
    getAccessibleProps,

    // Utility functions
    isHighContrast,
    prefersReducedMotion,
  };
};

export default useAccessibility;
